import { useEffect, useRef, useState } from "react";

import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useShallow } from "zustand/react/shallow";

import { getMonthlyReservation, fetchVehicleReservations } from "@/apis/reservation";
import Layout from "@/components/Layout";
import Tab from "@/components/Tab";
import Calendar from "@/pages/Home/components/Calendar";
import DailyReservationModal from "@/pages/Home/components/DailyReservationModal";
import DailyView from "@/pages/Home/components/DailyView";
import PlaceTree from "@/pages/Home/components/PlaceTree";
import QuickReservationModal from "@/pages/Home/components/QuickReservationModal";
import ReservationDetailModal from "@/pages/Home/components/ReservationDetailModal";
import VehicleQuickReservationModal from "@/pages/Home/components/VehicleQuickReservationModal";
import VehicleDetailModal from "@/pages/Home/components/VehicleDetailModal";
import VehicleDailyModal from "@/pages/Home/components/VehicleDailyModal";
import VehicleDailyView from "@/pages/Home/components/VehicleDailyView";
import VehicleWeeklyView from "@/pages/Home/components/VehicleWeeklyView";
import ViewModeToolbar from "@/pages/Home/components/ViewModeToolbar";
import WeeklyView from "@/pages/Home/components/WeeklyView";
import useCalendarStore from "@/store/calendarStore";
import useModal from "@/hooks/useModal";
import useViewMode from "@/hooks/useViewMode";
import { cn } from "@/utils/cn";
import type { DailyReservationData, DailyReservation } from "@/types/reservation";
import type { VehicleReservation, VehicleReservationStatus } from "@/types/vehicleReservation";

// ============================================================
// 장소 예약 뷰
// ============================================================
const PlaceReservationView = () => {
  const detailModal = useModal();
  const reserveModal = useModal();
  const containerRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const [mainHeight, setMainHeight] = useState<number | undefined>(undefined);
  const [showTree, setShowTree] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<DailyReservationData | null>(null);
  const [isDaySelected, setIsDaySelected] = useState(false);

  const [
    date,
    firstDayOfMonth,
    viewMode,
    selectedPlace,
    ,
    setMonthlyReservations,
  ] = useCalendarStore(
    useShallow(state => [
      state.date,
      state.firstDayOfMonth,
      state.viewMode,
      state.selectedPlace,
      state.monthlyReservations,
      state.setMonthlyReservations,
    ]),
  );

  const { isAdmin: isAdminPlace } = useViewMode();

  // 캘린더 영역 높이 측정 → 장소 패널에 동기화
  useEffect(() => {
    if (!mainRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setMainHeight(entry.contentRect.height);
    });
    observer.observe(mainRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selectedPlace) return;
    const load = async () => {
      let reservations = await getMonthlyReservation(
        firstDayOfMonth.format("YYYYMM"),
        selectedPlace.plcCd,
      );
      const hiddenStatuses = isAdminPlace
        ? ["deleted"]
        : ["reject", "cancel", "deleted"];
      reservations = reservations
        .map((r: DailyReservation) => ({
          ...r,
          data: r.data.filter(
            (d: DailyReservationData) => !hiddenStatuses.includes(d.sttCd),
          ),
        }))
        .filter((r: DailyReservation) => r.data.length > 0);
      setMonthlyReservations(reservations);
    };
    load();
  }, [firstDayOfMonth, selectedPlace, setMonthlyReservations, isAdminPlace]);

  // Clear card selection and day selection when view mode changes
  useEffect(() => {
    setSelectedReservation(null);
    setIsDaySelected(viewMode === "daily");
  }, [viewMode]);

  // Outside click: clear selections when clicking outside the entire component
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setSelectedReservation(null);
        if (viewMode !== "daily") setIsDaySelected(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [viewMode]);

  const handleDateClick = (day: Dayjs) => {
    useCalendarStore.getState().setDate(day);
    setIsDaySelected(true);
  };

  const handleClearSelections = () => {
    setSelectedReservation(null);
    if (viewMode !== "daily") setIsDaySelected(false);
  };

  const handleReservationSelect = (reservation: DailyReservationData | null) => {
    setSelectedReservation(reservation);
  };

  // 상세 버튼 정책:
  // - 일일: 카드 선택 → 개별 상세 / 카드 미선택 → 해당 일자 리스트
  // - 주간/월간: 해당 일자 리스트 (빈 날짜도 빈 리스트)
  const handleDetail = () => {
    detailModal.onOpen();
  };

  const handleReserveSuccess = () => {
    reserveModal.onClose();
    if (selectedPlace) {
      getMonthlyReservation(
        firstDayOfMonth.format("YYYYMM"),
        selectedPlace.plcCd,
      ).then(setMonthlyReservations);
    }
  };

  return (
    <div ref={containerRef} onClick={handleClearSelections}>
      <div className="flex items-start gap-4">
        {/* PlaceTree sidebar - desktop */}
        <div className="hidden w-52 shrink-0 lg:block">
          <div
            className="flex flex-col rounded-lg border border-gray-200 bg-white shadow-sm"
            style={mainHeight ? { height: mainHeight } : undefined}
          >
            <div className="shrink-0 border-b border-gray-100 px-3 py-2 text-sm font-medium text-gray-700">
              장소
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <PlaceTree />
            </div>
          </div>
        </div>

        {/* Main area */}
        <div ref={mainRef} className="min-w-0 flex-1">
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm md:px-5 md:py-5">
            <div className="flex flex-col gap-4">
              {/* Mobile place selector - above toolbar */}
              <div className="relative lg:hidden">
                <button
                  type="button"
                  onClick={() => setShowTree(!showTree)}
                  className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm transition-colors hover:border-primary/50"
                >
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium text-gray-800">
                      {selectedPlace?.plcNm || "장소를 선택하세요"}
                    </span>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={cn("h-4 w-4 text-gray-400 transition-transform", showTree && "rotate-180")}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {showTree && (
                  <div className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    <PlaceTree onSelect={() => setShowTree(false)} />
                  </div>
                )}
              </div>

              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div onClick={(e) => e.stopPropagation()}>
                <ViewModeToolbar
                  onDetail={handleDetail}
                  onReserve={() => reserveModal.onOpen()}
                  hasDetailData={viewMode === "daily" || isDaySelected}
                />
              </div>

              {viewMode === "monthly" && <Calendar onDateClick={handleDateClick} isDaySelected={isDaySelected} />}
              {viewMode === "daily" && (
                <DailyView
                  selectedReservation={selectedReservation}
                  onSelect={handleReservationSelect}
                />
              )}
              {viewMode === "weekly" && <WeeklyView isDaySelected={isDaySelected} onDateClick={handleDateClick} />}
            </div>
          </div>
        </div>
      </div>

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        {/* 일일 + 카드 선택됨 → 개별 상세 */}
        {detailModal.isOpen && viewMode === "daily" && selectedReservation && (
          <ReservationDetailModal
            reservation={selectedReservation}
            onClose={detailModal.onClose}
          />
        )}

        {/* 일일 + 카드 미선택, 주간, 월간 → 해당 일자 리스트 */}
        {detailModal.isOpen && !(viewMode === "daily" && selectedReservation) && (
          <DailyReservationModal
            date={date}
            onClose={detailModal.onClose}
          />
        )}

        {reserveModal.isOpen && (
          <QuickReservationModal
            onClose={reserveModal.onClose}
            onSuccess={handleReserveSuccess}
          />
        )}
      </div>
    </div>
  );
};

// ============================================================
// 차량 예약 뷰
// ============================================================
const vehicleStatusToSttCd: Record<VehicleReservationStatus, string> = {
  pending: "request",
  approved: "approve",
  rejected: "reject",
  cancelled: "cancel",
  deleted: "deleted",
};

const convertVehicleToMonthly = (vehicleData: VehicleReservation[]): DailyReservation[] => {
  const grouped = new Map<string, DailyReservationData[]>();
  vehicleData.forEach(r => {
    const parts = r.drivingTime?.split("~") || ["", ""];
    const start = (parts[0] || "").trim();
    const end = (parts[1] || "").trim();
    const data: DailyReservationData = {
      rsvtId: r.id,
      useCnts: r.purpose,
      plcCd: "",
      plcNm: r.destination,
      rsvtDt: dayjs(r.reservationDate).format("YYYYMMDD"),
      startTime: start ? start.replace(":", "") + "00" : "",
      endTime: end ? end.replace(":", "") + "00" : "",
      mbrId: "",
      userName: r.applicantName,
      sttCd: vehicleStatusToSttCd[r.status] as DailyReservationData["sttCd"],
      telNo: r.applicantPhone,
    };
    const day = r.reservationDate;
    if (!grouped.has(day)) grouped.set(day, []);
    grouped.get(day)!.push(data);
  });
  return Array.from(grouped.entries()).map(([day, data]) => ({ day, data }));
};

const VehicleReservationView = () => {
  const detailModal = useModal();
  const reserveModal = useModal();
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedReservation, setSelectedReservation] = useState<DailyReservationData | null>(null);
  const [isDaySelected, setIsDaySelected] = useState(false);

  const [
    date,
    firstDayOfMonth,
    viewMode,
    monthlyReservations,
    setMonthlyReservations,
  ] = useCalendarStore(
    useShallow(state => [
      state.date,
      state.firstDayOfMonth,
      state.viewMode,
      state.monthlyReservations,
      state.setMonthlyReservations,
    ]),
  );

  const { isAdmin: isAdminVehicle } = useViewMode();

  const loadVehicleData = async () => {
    const rsvtDt = firstDayOfMonth.format("YYYYMM");
    const vehicleData = await fetchVehicleReservations(rsvtDt);
    let converted = convertVehicleToMonthly(vehicleData);
    const hiddenStatuses = isAdminVehicle
      ? ["deleted"]
      : ["reject", "cancel", "deleted"];
    converted = converted
      .map(r => ({
        ...r,
        data: r.data.filter(
          d => !hiddenStatuses.includes(d.sttCd),
        ),
      }))
      .filter(r => r.data.length > 0);
    setMonthlyReservations(converted);
  };

  useEffect(() => {
    loadVehicleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstDayOfMonth]);

  // Clear card selection and day selection when view mode changes
  useEffect(() => {
    setSelectedReservation(null);
    setIsDaySelected(viewMode === "daily");
  }, [viewMode]);

  // Outside click: clear selections when clicking outside the entire component
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setSelectedReservation(null);
        if (viewMode !== "daily") setIsDaySelected(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [viewMode]);

  const handleDateClick = (day: Dayjs) => {
    useCalendarStore.getState().setDate(day);
    setIsDaySelected(true);
  };

  const handleClearSelections = () => {
    setSelectedReservation(null);
    if (viewMode !== "daily") setIsDaySelected(false);
  };

  const handleReservationSelect = (reservation: DailyReservationData | null) => {
    setSelectedReservation(reservation);
  };

  const handleReserveSuccess = () => {
    reserveModal.onClose();
    loadVehicleData();
  };

  const dateKey = date.format("YYYYMMDD");
  const dailyMatch = monthlyReservations.find(r => r.day === dateKey);
  const dateHasReservation = !!(dailyMatch && dailyMatch.data.length > 0);
  const isPastDate = date.isBefore(dayjs(), "day");

  const getReserveTooltip = () => {
    if (isPastDate) return "이전 날짜이므로 예약이 불가합니다.";
    if (dateHasReservation) return "해당 날짜에 이미 예약이 존재합니다.";
    return "";
  };

  const handleDetail = () => {
    detailModal.onOpen();
  };

  return (
    <div ref={containerRef} onClick={handleClearSelections}>
      <div className="min-w-0 flex-1">
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm md:px-5 md:py-5">
          <div className="flex flex-col gap-4">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div onClick={(e) => e.stopPropagation()}>
              <ViewModeToolbar
                onDetail={handleDetail}
                onReserve={() => reserveModal.onOpen()}
                hasDetailData={viewMode === "daily" || isDaySelected}
                reserveDisabled={dateHasReservation}
                reserveTooltip={getReserveTooltip()}
              />
            </div>

            {viewMode === "monthly" && <Calendar onDateClick={handleDateClick} isDaySelected={isDaySelected} />}
            {viewMode === "daily" && (
              <VehicleDailyView
                selectedReservation={selectedReservation}
                onSelect={handleReservationSelect}
              />
            )}
            {viewMode === "weekly" && <VehicleWeeklyView isDaySelected={isDaySelected} onDateClick={handleDateClick} />}
          </div>
        </div>
      </div>

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        {/* 일일 + 카드 선택됨 → 개별 상세 */}
        {detailModal.isOpen && viewMode === "daily" && selectedReservation && (
          <VehicleDetailModal
            reservationData={selectedReservation}
            onClose={detailModal.onClose}
          />
        )}

        {/* 일일 + 카드 미선택, 주간, 월간 → 해당 일자 리스트 */}
        {detailModal.isOpen && !(viewMode === "daily" && selectedReservation) && (
          <VehicleDailyModal
            date={date}
            onClose={detailModal.onClose}
          />
        )}

        {reserveModal.isOpen && (
          <VehicleQuickReservationModal
            onClose={reserveModal.onClose}
            onSuccess={handleReserveSuccess}
          />
        )}
      </div>
    </div>
  );
};

// ============================================================
// Home
// ============================================================
const Home = () => {
  const resetCalendar = useCalendarStore(state => state.resetCalendar);

  useEffect(() => {
    resetCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <div className="flex flex-col gap-4">
        <Tab variant="solid">
          <Tab.Item label="장소 예약">
            <div className="pt-4">
              <PlaceReservationView />
            </div>
          </Tab.Item>
          <Tab.Item label="차량 예약">
            <div className="pt-4">
              <VehicleReservationView />
            </div>
          </Tab.Item>
        </Tab>
      </div>
    </Layout>
  );
};

export default Home;
